import React from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import { addToast } from '../../../lib/redux/actions/toast';
import { consultantSingleFetch, consultantSingleUpdate } from '../../../lib/redux/actions/consultant';
import { connect } from 'react-redux';
import MASpinner from '../../common/MASpinner';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { renderCheckbox, renderField, renderSelect, ReactSelectCreatable } from '../../../lib/helper/form';
import { cashbookBalanceFormatter, niceDateFormatter, userNameFormatter } from '../../../lib/helper/formatting';
import { isAdmin, isClient, isSeniorStaff, isStaff } from '../../../lib/helper/authorisation';
import NewPrimaryUserTypeahead from '../../fields/NewPrimaryUserTypeahead';
import ConfirmButton from '../../common/ConfirmButton';
import { consultantTypeListFetch } from '../../../lib/redux/actions/consultantType';
import { consultantSpecialityListFetch, consultantSpecialitySingleCreate } from '../../../lib/redux/actions/consultantSpeciality';

const selector = formValueSelector('AccountDetailsForm');

const mapStateToProps = (state) => ({
  ...state.consultantTypes,
  ...state.consultantSpecialities,
  fastTrackValue: selector(state, 'fastTrack'),
});
const mapDispatchToProps = {
  consultantTypeListFetch,
  consultantSpecialityListFetch,
  consultantSpecialitySingleCreate,
  consultantSingleUpdate,
  consultantSingleFetch,
  addToast,
};

class AccountDetailsForm extends React.Component {
  static buildInitValues(consultant) {
    if (!consultant) {
      return {
        isActive: true,
        notifyPrimaryUserOnNewInvoiceBatch: false,
        notifyPrimaryUserOnWeeklyInvoiceSummary: false,
        useAutomatedChasing: true,
        fastTrack: false,
      };
    }
    return {
      code: consultant.code,
      salutation: consultant.salutation ?? '',
      name: consultant.name,
      gmcReferenceNumber: consultant.gmcReferenceNumber,
      consultantSpecialities: (consultant.consultantSpecialities ?? []).map((s) => ({ value: s['@id'], label: s.name })),
      invoicePrefix: consultant.invoicePrefix,
      isActive: consultant.isActive,
      notifyPrimaryUserOnNewInvoiceBatch: consultant.notifyPrimaryUserOnNewInvoiceBatch,
      notifyPrimaryUserOnWeeklyInvoiceSummary: consultant.notifyPrimaryUserOnWeeklyInvoiceSummary,
      consultantTypes: consultant.consultantTypes.map((ct) => ct['@id']),
      phoneNumber: consultant.phoneNumber,
      clientAccountNumber: consultant.clientAccountNumber,
      clientSortCode: consultant.clientSortCode,
      personalAccountNumber: consultant.personalAccountNumber,
      personalAccountNumber2: consultant.personalAccountNumber2,
      personalSortCode: consultant.personalSortCode,
      personalSortCode2: consultant.personalSortCode2,
      personalAccountName: consultant.personalAccountName,
      personalAccountName2: consultant.personalAccountName2,
      personalAccountChangeDate: consultant.personalAccountChangeDate,
      secretaryName: consultant.secretaryName,
      secretaryPhoneNumber: consultant.secretaryPhoneNumber,
      secretaryEmail: consultant.secretaryEmail,
      billingStartDate: consultant.billingStartDate,
      billingEndDate: consultant.billingEndDate,
      useAutomatedChasing: consultant.useAutomatedChasing,
      fastTrack: consultant.fastTrack,
    };
  }

  constructor(props) {
    super(props);
    const { consultant, initialize } = this.props;

    this.state = {
      selectedPrimaryUser: null,
      clearTypeaheads: false,
      selectedTypeCount: consultant ? consultant.consultantTypes.length : 0,
    };
    initialize(AccountDetailsForm.buildInitValues(consultant));
  }

  componentDidMount() {
    this.props.consultantTypeListFetch();
    this.props.consultantSpecialityListFetch();
  }

  clearTypeaheads() {
    this.setState(() => {
      return { clearTypeaheads: true };
    });
  }

  typeaheadCleared() {
    if (this.state.clearTypeaheads) {
      this.setState(() => {
        return { clearTypeaheads: false };
      });
    }
  }

  onSubmit(values) {
    const { consultantSingleUpdate, addToast, consultant, isCreate = false, onCreate = undefined } = this.props;

    const payload = { ...values };

    if (isClient()) {
      delete payload.name;
      delete payload.code;
      delete payload.isActive;
      delete payload.billingStartDate;
      delete payload.billingEndDate;
    }

    const oldTypeIds = (consultant?.consultantTypes ?? []).map((ct) => ct['@id']).sort().join(',');
    const newTypeIds = (values.consultantTypes || []).sort().join(',');
    const refreshConsultantData = !isCreate && oldTypeIds !== newTypeIds;

    if (this.state.selectedPrimaryUser !== null) {
      payload['primaryUser'] = this.state.selectedPrimaryUser['@id'];
    }

    if (payload.salutation === '') {
      payload.salutation = null;
    }

    if (payload.consultantSpecialities) {
      payload.consultantSpecialities = payload.consultantSpecialities.map((option) => option.value);
    }

    if (isCreate && onCreate) {
      return onCreate(payload);
    } else {
      return consultantSingleUpdate(payload, consultant.id).then((response) => {
        addToast('Account Details Successfully Updated', true);
        this.props.initialize({ ...values, fastTrack: response.fastTrack });
        this.setState({ selectedTypeCount: (values.consultantTypes || []).length });
        if (refreshConsultantData) {
          this.props.consultantSingleFetch(consultant.id, true);
        }
      });
    }
  }

  render() {
    const {
      consultant,
      handleSubmit,
      error,
      pristine,
      submitting,
      isUpdatingConsultant,
      isFetchingConsultantTypes,
      consultantTypes = [],
      consultantSpecialities = [],
      consultantSpecialitySingleCreate,
      isCreate = false,
      fastTrackValue,
    } = this.props;
    const firstError = error ? error[Object.keys(error)[0]] : null;

    if (isFetchingConsultantTypes) {
      return <MASpinner />;
    }
    const readOnlyTypes = isClient() || isStaff(true);

    const specialitiesOptions = [];
    consultantSpecialities.forEach((consultantSpeciality) => {
      specialitiesOptions.push({ value: consultantSpeciality['@id'], label: consultantSpeciality.name });
    });


    const balance = consultant ? consultant.cashbookBalance : null;
    const reconciledAt = consultant ? consultant.cashbookReconciledAt : null;
    const formattedBalance = cashbookBalanceFormatter(balance);


    return (
      <div className="crud__section mb-3">
        <div className="crud__section-icon">
          <i className="fas fa-cog" />
        </div>
        <h2 className="crud__section-header">Account details</h2>
        {error && <div className="alert alert-danger">{firstError}</div>}
        <Form onSubmit={handleSubmit(this.onSubmit.bind(this))}>
          <div className="row">
            <div className="col-sm-4 col-xl-2">
              {isCreate || isAdmin() ? (
                <Field name="code" label="Account Code" id="code" type="text" component={renderField} />
              ) : (
                <Form.Group controlId="formGroupCode">
                  <Form.Label>Account Code</Form.Label>
                  <Form.Control type="text" readOnly value={consultant.code} />
                </Form.Group>
              )}
            </div>
            <div className="col-sm-4 col-xl-2">
              <Field
                name="salutation"
                label="Salutation"
                disabled={isClient() || isStaff(true)}
                component={renderSelect}
                emptyLabel="-"
                options={[
                  { value: 'Mr', text: 'Mr' },
                  { value: 'Mrs', text: 'Mrs' },
                  { value: 'Ms', text: 'Ms' },
                  { value: 'Miss', text: 'Miss' },
                  { value: 'Dr', text: 'Dr' },
                  { value: 'Prof', text: 'Prof' },
                ]}
              />
            </div>
            <div className="col-sm-8 col-xl-3">
              <Field
                name="name"
                readOnly={isClient() || isStaff(true)}
                label="Account Name"
                placeholder="e.g. Doctor Smith"
                type="text"
                component={renderField}
              />
            </div>

            {isCreate && (
              <div className="col-sm-8 col-xl-5">
                <NewPrimaryUserTypeahead
                  selectedUsers={this.state.selectedPrimaryUser ? [this.state.selectedPrimaryUser] : []}
                  required
                  shouldClear={this.state.clearTypeaheads}
                  hasCleared={this.typeaheadCleared.bind(this)}
                  onChange={(selected) => {
                    this.setState(() => {
                      return { selectedPrimaryUser: selected[0] };
                    });
                  }}
                />
              </div>
            )}
            {!isCreate && consultant.primaryUser && (
              <div className="col-sm-8 col-xl-5">
                <Form.Group controlId="formGroupCode">
                  <Form.Label>Primary User</Form.Label>
                  <Form.Control type="text" readOnly value={`${userNameFormatter(consultant.primaryUser)} (${consultant.primaryUser.email})`} />
                </Form.Group>
              </div>
            )}
          </div>
          <hr />
          <div className="row">
            <fieldset className="col-sm-12">
              <div className="row">
                <legend className="col-sm-12">Consultant Specifics</legend>
                <div className="col-sm-6 col-xl-2">
                  <Field
                    name="consultantTypes"
                    component={({ input }) => (
                      <>
                        <Form.Label>Consultant Type</Form.Label>
                        <Row>
                        {consultantTypes.map((ct) => (
                            <Col>
                          <Form.Check
                            key={ct['@id']}
                            type="switch"
                            id={`consultant-type-${ct.code}`}
                            label={ct.displayName}
                            disabled={readOnlyTypes}
                            checked={(input.value || []).includes(ct['@id'])}
                            onChange={(e) => {
                              const current = input.value || [];
                              const updated = e.target.checked
                                ? [...current, ct['@id']]
                                : current.filter((id) => id !== ct['@id']);
                              this.setState({ selectedTypeCount: updated.length });
                              input.onChange(updated);
                            }}
                          />
                            </Col>
                        ))}
                        </Row>
                      </>
                    )}
                  />
                </div>
                <div className="col-sm-6 col-xl-2">
                  <Field name="gmcReferenceNumber" readOnly={isClient() || isStaff(true)} label="GMC Number" type="text" component={renderField} />
                </div>

                <div className="col-sm-6 col-xl-2">
                  <Field name="invoicePrefix" readOnly={isClient() || isStaff(true)} label="Invoice Prefix" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-2">
                  <Field
                    name="billingStartDate"
                    readOnly={isClient()}
                    label="Billing Start Date"
                    type="date"
                    value={consultant?.billingStartDate}
                    component={renderField}
                  />
                </div>
                <div className="col-sm-6 col-xl-2">
                  <Field
                    name="billingEndDate"
                    readOnly={isClient()}
                    label="Billing End Date"
                    type="date"
                    value={consultant?.billingEndDate}
                    component={renderField}
                  />
                </div>
                <div className="col-sm-6 col-xl-4">
                  <Field
                    name="consultantSpecialities"
                    readOnly={isClient() || isStaff(true)}
                    label="Speciality"
                    type="text"
                    component={ReactSelectCreatable}
                    options={specialitiesOptions}
                    onCreateOption={(inputValue) =>
                      consultantSpecialitySingleCreate(inputValue).then((response) => ({
                        value: response['@id'],
                        label: response.name,
                      }))
                    }
                    isMulti={true}
                  />
                </div>
              </div>
            </fieldset>
          </div>

          <hr />

          <div className="row">
            <fieldset className="col-sm-12">
              <div className="row">
                <legend className="col-sm-12">Personal Account Details</legend>
                <div className="col-sm-6 col-xl-3">
                  <Field name="personalAccountName" readOnly={isClient() || isStaff(true)} label="Current Account Name" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field
                    name="personalAccountNumber"
                    readOnly={isClient() || isStaff(true)}
                    label="Current Account Number"
                    type="text"
                    component={renderField}
                  />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field name="personalSortCode" readOnly={isClient() || isStaff(true)} label="Current Sort Code" type="text" component={renderField} />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6 col-xl-3">
                  <Field name="personalAccountName2" readOnly={isClient() || isStaff(true)} label="Old Account Name" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field name="personalAccountNumber2" readOnly={isClient() || isStaff(true)} label="Old Account Number" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field name="personalSortCode2" readOnly={isClient() || isStaff(true)} label="Old Sort Code" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field
                    name="personalAccountChangeDate"
                    readOnly={isClient()}
                    label="Account Change Date"
                    type="date"
                    value={consultant?.personalAccountChangeDate}
                    component={renderField}
                  />
                </div>
              </div>
            </fieldset>
          </div>

          <hr />

          <div className="row">
            <fieldset className="col-sm-12 col-xl-6">
              <div className="row">
                <legend className="col-sm-12">Client Account Details</legend>
                <div className="col-sm-6 col-xl-4">
                  <Field name="phoneNumber" readOnly={isClient() || isStaff(true)} label="Phone Number" type="tel" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-4">
                  <Field name="clientAccountNumber" readOnly={isClient() || isStaff(true)} label="Account Number" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-4">
                  <Field name="clientSortCode" readOnly={isClient() || isStaff(true)} label="Sort Code" type="text" component={renderField} />
                </div>
              </div>
            </fieldset>
            {isAdmin() && formattedBalance && reconciledAt && (
                <fieldset className="col-sm-12 col-xl-6">
                  <div className="row">
                    <legend className="col-sm-12">Cashbook Balance</legend>
                    <div className="col-sm-6 col-xl-4">
                      <p><span className="spoiler">{formattedBalance}</span> at {niceDateFormatter(reconciledAt, 'DD MMMM YYYY')}</p>
                    </div>
                  </div>
                </fieldset>
              )}
          </div>
          <hr />
          <div className="row">
            <fieldset className="col-sm-12">
              <div className="row">
                <legend className="col-sm-12">Secretary Details</legend>
                <div className="col-sm-6 col-xl-3">
                  <Field name="secretaryName" readOnly={isClient() || isStaff(true)} label="Name" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-3">
                  <Field name="secretaryPhoneNumber" readOnly={isClient() || isStaff(true)} label="Phone Number" type="text" component={renderField} />
                </div>
                <div className="col-sm-6 col-xl-6">
                  <Field name="secretaryEmail" readOnly={isClient() || isStaff(true)} label="Email" type="email" component={renderField} />
                </div>
              </div>
            </fieldset>
          </div>

          <hr />
          {/*{isSeniorStaff() && <Field name="isActive" label="Active?" id="is-active" component={renderCheckbox}/>}*/}
          {/*<br/>*/}
          <Field
            name="notifyPrimaryUserOnNewInvoiceBatch"
            disabled={isClient() || isStaff(true)}
            label="Receive emails when invoices are uploaded?"
            id="notify-primary-user-on-new-invoice-batch"
            component={renderCheckbox}
          />
          <br />
          <Field
            name="notifyPrimaryUserOnWeeklyInvoiceSummary"
            disabled={isClient() || isStaff(true)}
            label="Receive weekly summary of all invoices uploaded?"
            id="notify-primary-user-on-weekly-invoice-summary"
            component={renderCheckbox}
          />

          {/* MED-1000. Chasing and fast track are MediAccounts' settings about a consultant, not
              the consultant's own preferences - they were disabled for clients but still on the
              screen. The API drops both for anyone below senior staff, so showing them to a
              consultant only advertises a control that does nothing. */}
          {!isClient() && (
            <>
              <br />
              <Field
                name="useAutomatedChasing"
                disabled={isStaff(true)}
                label="Use Automated Invoice Chasing?"
                id="use-automated-chasing"
                component={renderCheckbox}
              />
              <br />
              <Field
                name="fastTrack"
                disabled={!isSeniorStaff() || this.state.selectedTypeCount > 1}
                label="Fast Track?"
                id="fast-track"
                component={renderCheckbox}
              />
              {fastTrackValue && this.state.selectedTypeCount > 1 && (
                <Form.Text className="text-danger font-italic">
                  <small>This consultant has more than one type — fast track will be disabled automatically on save.</small>
                </Form.Text>
              )}
              {!fastTrackValue && this.state.selectedTypeCount > 1 && (
                <Form.Text className="text-muted font-italic">
                  <small>Fast track is not available to consultants with more than one Consultant Type.</small>
                </Form.Text>
              )}
            </>
          )}
          <br />
          {(isSeniorStaff() || isClient()) &&
            (isUpdatingConsultant ? (
              <MASpinner />
            ) : !isCreate && this.state.selectedTypeCount > 1 && fastTrackValue && !(pristine || submitting) ? (
              <ConfirmButton
                confirmText="Update"
                confirmedText="Fast track is not available with multiple Consultant Types and will be disabled — click again to confirm"
                confirmClasses="btn btn-primary btn-block"
                confirmedClasses="btn btn-warning btn-block"
                confirmedAction={handleSubmit(this.onSubmit.bind(this))}
                confirmTimeout={5000}
              />
            ) : (
              <button type="submit" disabled={pristine || submitting} className="btn btn-primary btn-block">
                {isCreate ? 'Create Consultant' : 'Update'}
              </button>
            ))}
        </Form>
      </div>
    );
  }
}

const validate = (values) => {
  const errors = {};
  if (!values.consultantSpecialities || values.consultantSpecialities.length === 0) {
    errors.consultantSpecialities = 'At least one speciality is required';
  }
  return errors;
};

export default reduxForm({
  form: 'AccountDetailsForm',
  validate,
})(connect(mapStateToProps, mapDispatchToProps)(AccountDetailsForm));
